import { useState } from "react";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatApiError, useAuth } from "@/context/AuthContext";

export const AuthDialog = () => {
  const { login, register } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      if (tab === "signin") {
        await login(email, password);
        toast.success("Welcome back to the archive");
      } else {
        await register(email, password, name);
        toast.success("Your reading room is ready");
      }
      setOpen(false);
      setEmail("");
      setPassword("");
      setName("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="signin-btn"
          className="rounded-full transition-transform duration-200 hover:-translate-y-0.5"
        >
          <LogIn className="mr-1 h-4 w-4" />
          Sign in
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="auth-dialog" className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Reading room access</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-4 space-y-3">
          {tab === "register" && (
            <Input
              data-testid="auth-name-input"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={onKeyDown}
            />
          )}
          <Input
            data-testid="auth-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <Input
            data-testid="auth-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {error && (
            <p data-testid="auth-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button
            data-testid="auth-submit-btn"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full"
          >
            {busy ? "One moment…" : tab === "signin" ? "Sign in" : "Create account"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Reading is open to all. Signing in lets you archive and remove tomes.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
